import { default as ReactSelect, components } from "react-select";
import type {
    Form as FormComponent,
    StyleFunction,
} from "@venuepos/react-common";
import {
    Button,
    CheckBox,
    InputControl,
    InputLabel,
    Loading,
    Picker,
    Surface,
    useThemedStyle,
} from "@venuepos/react-common";
import type { GQApprovalInput } from "graphql-sdk";
import {
    useApprovalFormQuery,
    useApprovalTokenGroupsQuery,
    useApprovalTokensQuery,
} from "graphql-sdk";
import { useTranslation } from "locales";
import React from "react";

const Option = props => {
    return (
        <>
            <components.Option {...props}>
                <input
                    type="checkbox"
                    checked={props.isSelected}
                    onChange={() => null}
                />{" "}
                <label>{props.label}</label>
            </components.Option>
        </>
    );
};

export function Form(props: {
    form: FormComponent<GQApprovalInput>;
    onSubmit: () => void;
    submitButton: [string, string];
    isEditing?: boolean;
}) {
    const [t] = useTranslation();
    const [{ values, errors }, { setValue, handleSubmit }] = props.form;
    const styles = useThemedStyle(styleFunc);
    const { data, loading } = useApprovalFormQuery();

    const { data: tokenData, loading: tokenLoading } = useApprovalTokensQuery({
        variables: { pagination: { pageSize: 9999 } },
    });

    const { data: tokenGroupData, loading: tokenGroupLoading } =
        useApprovalTokenGroupsQuery({
            variables: { pagination: { pageSize: 9999 } },
        });

    if (loading || tokenLoading || tokenGroupLoading) {
        return <Loading />;
    }

    if (!values || !data) {
        return null;
    }

    return (
        <>
            <Surface style={styles.surfaceContainer}>
                <InputControl
                    error={errors.active}
                    description={t(
                        "backoffice.approval.active_description",
                        "Should the approval be active or not"
                    )}
                >
                    <CheckBox
                        label={t("common.active", "Active")}
                        value={values.active}
                        onValueChange={checked => setValue("active", checked)}
                        testID="active"
                    />
                </InputControl>
                <InputControl
                    error={errors.type}
                    description={t(
                        "backoffice.approval.type_description",
                        "The type of approval."
                    )}
                >
                    <Picker
                        label={t("common.type", "Type")}
                        required={true}
                        onValueChange={value => {
                            setValue("type", value);
                        }}
                        selectedValue={values.type || ""}
                        disabled={props.isEditing}
                    >
                        <Picker.Item
                            label={t(
                                "backoffice.approval.choose_type",
                                "Choose type"
                            )}
                            value=""
                        />
                        <Picker.Item
                            key="DISCOUNT"
                            value="DISCOUNT"
                            label={t(
                                "backoffice.approval.types.DISCOUNT",
                                "Add discount"
                            )}
                            disabled={
                                data.approvals.data.find(
                                    itr => itr.active && itr.type === "DISCOUNT"
                                ) !== undefined && values.type !== "DISCOUNT"
                            }
                        />
                        <Picker.Item
                            key="OPEN_CASH_DRAWER"
                            value="OPEN_CASH_DRAWER"
                            label={t(
                                "backoffice.approval.types.OPEN_CASH_DRAWER",
                                "Open cashdrawer"
                            )}
                            disabled={
                                data.approvals.data.find(
                                    itr =>
                                        itr.active &&
                                        itr.type === "OPEN_CASH_DRAWER"
                                ) !== undefined &&
                                values.type !== "OPEN_CASH_DRAWER"
                            }
                        />
                        <Picker.Item
                            key="REFUND"
                            value="REFUND"
                            label={t(
                                "backoffice.approval.types.REFUND",
                                "Refund amount"
                            )}
                            disabled={
                                data.approvals.data.find(
                                    itr => itr.active && itr.type === "REFUND"
                                ) !== undefined && values.type !== "REFUND"
                            }
                        />
                        <Picker.Item
                            key="RETURN"
                            value="RETURN"
                            label={t(
                                "backoffice.approval.types.RETURN",
                                "Return products"
                            )}
                            disabled={
                                data.approvals.data.find(
                                    itr => itr.active && itr.type === "RETURN"
                                ) !== undefined && values.type !== "RETURN"
                            }
                        />
                    </Picker>
                </InputControl>
                <InputControl>
                    <InputLabel>
                        {t("backoffice.approval.tokens", "Tokens")}
                    </InputLabel>
                    <ReactSelect
                        options={tokenData.approvalTokens.data
                            .map(itr => {
                                return { value: itr.id, label: itr.name };
                            })
                            .sort((a, b) => a.label.localeCompare(b.label))}
                        // This must be set to avoid having the dropdown hidden by other inputs
                        menuPortalTarget={document.body}
                        isMulti
                        closeMenuOnSelect={false}
                        hideSelectedOptions={false}
                        components={{
                            Option,
                        }}
                        value={values.tokenIds.map(itr => {
                            const token = tokenData.approvalTokens.data.find(
                                itr2 => itr2.id === itr
                            );
                            return {
                                value: token?.id,
                                label: token?.name,
                            };
                        })}
                        onChange={value => {
                            setValue(
                                "tokenIds",
                                value.map(itr => itr.value)
                            );
                        }}
                    />
                </InputControl>

                <InputControl>
                    <InputLabel>
                        {t("backoffice.approval.token_groups", "Token groups")}
                    </InputLabel>
                    <ReactSelect
                        options={tokenGroupData.approvalTokenGroups.data
                            .map(itr => {
                                return { value: itr.id, label: itr.name };
                            })
                            .sort((a, b) => a.label.localeCompare(b.label))}
                        // This must be set to avoid having the dropdown hidden by other inputs
                        menuPortalTarget={document.body}
                        isMulti
                        closeMenuOnSelect={false}
                        hideSelectedOptions={false}
                        components={{
                            Option,
                        }}
                        value={values.tokenGroupIds.map(itr => {
                            const tokenGroup =
                                tokenGroupData.approvalTokenGroups.data.find(
                                    itr2 => itr2.id === itr
                                );
                            return {
                                value: tokenGroup?.id,
                                label: tokenGroup?.name,
                            };
                        })}
                        onChange={value => {
                            setValue(
                                "tokenGroupIds",
                                value.map(itr => itr.value)
                            );
                        }}
                    />
                </InputControl>

                <Button onPress={handleSubmit(props.onSubmit)}>
                    {t(props.submitButton[0], props.submitButton[1])}
                </Button>
            </Surface>
        </>
    );
}

const styleFunc: StyleFunction = theme => ({
    surfaceContainer: {
        marginVertical: theme.spacingScale / 2,
    },
});
